/**
 * Diese Komponente bindet dann das jeweilige Modul ein, übergibt den User, sowie die verfügbaren Socialmediakanäle und die jeweiligen Pfade und Token.
 */

import 'regenerator-runtime/runtime'
import React, { useCallback, useEffect, useState } from 'react'
import { applyMiddleware, createStore } from 'redux'
import { Provider, useDispatch, useSelector } from 'react-redux'
import createSagaMiddleware from 'redux-saga'
import { composeWithDevTools } from '@redux-devtools/extension'

import { getLicensePermissions, getTheme, getUser } from '../../../store/common/common.selectors'
import { Box, Grid, Stack, Theme, Typography } from '@mui/material'

// Topics
import ProfessocialTopics from '@professocial/professocialTopics/src/screens/ProfessocialTopics'
import { topicRootReducer } from '@professocial/professocialTopics/src/store/topicRoot.reducer'
import { topicRootSagas } from '@professocial/professocialTopics/src/store/topicRoot.sagas'
import { DisplayMode, SocialNetworkLicensePermissions } from '@professocial/professocialTypes/types/commonTypes'
import ButtonComponent from '@professocial/professocialFormComponents/FormComponents/ButtonComponent'
import TextFieldComponent from '@professocial/professocialFormComponents/FormComponents/TextFieldComponent'
import validateEmail from '../../../util/validateEmail'
import { useTranslation } from 'react-i18next'
import { makeStyles } from '@mui/styles'
import { GetCorePageSettingsRequest, NetworkType, UpdateCorePageSettingsRequest } from '@3m5/sm_publish-frontend-api'
import {
  deleteCoreFBPageSettings,
  deleteCoreInstaPageSettings,
  getCoreFBPageSettingsInitiated,
  getCoreInstaPageSettingsInitiated,
  updateCorePageSettingsInitiated,
} from '../../../store/dashboard/dashboard.actions'
import {
  getCoreFBPageSettings,
  getCoreInstaPageSettings,
  getFacebookPage,
  getInstagramPage, getReNewPageSettings,
} from '../../../store/dashboard/dashboard.selector'
import { CorePageSettings } from '@3m5/sm_publish-frontend-api'

const useStyles = makeStyles((theme: Theme) => ({
  text: {
    padding: theme.spacing(1),
  },
  text2: {
    padding: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  emailButton: {
    padding: '9px 15px',
  },
}))

// Topics
const sagaMiddleware = createSagaMiddleware()
const storeTopics = createStore(
  topicRootReducer,
  composeWithDevTools(
    applyMiddleware(sagaMiddleware),
  ),
)
sagaMiddleware.run(topicRootSagas)

const UserTopics: React.FC = () => {
  const theme: Theme = useSelector(getTheme)
  const classes = useStyles()
  const dispatch = useDispatch()
  const { t } = useTranslation()

  const facebookPage = useSelector(getFacebookPage)
  const instagramPage = useSelector(getInstagramPage)

  const [topicEmail, setTopicEmail] = useState<string | undefined>(undefined)
  const [selectedTab, setSelectedTab] = React.useState<'Facebook' | 'Instagram' | undefined>('Facebook')

  const dispatchGetCoreFBPageSettings = useCallback((params: GetCorePageSettingsRequest) => dispatch(getCoreFBPageSettingsInitiated(params)), [dispatch])
  const dispatchGetCoreInstaPageSettings = useCallback((params: GetCorePageSettingsRequest) => dispatch(getCoreInstaPageSettingsInitiated(params)), [dispatch])
  const dispatchUpdateCorePageSettings = useCallback((params: UpdateCorePageSettingsRequest) => dispatch(updateCorePageSettingsInitiated(params)), [dispatch])
  const dispatchDeleteCoreFBPageSettings = useCallback(() => dispatch(deleteCoreFBPageSettings()), [dispatch])
  const dispatchDeleteCoreInstaPageSettings = useCallback(() => dispatch(deleteCoreInstaPageSettings()), [dispatch])

  const storedCoreFBPageSettings: CorePageSettings | undefined = useSelector(getCoreFBPageSettings)
  const storedCoreInstaPageSettings: CorePageSettings | undefined = useSelector(getCoreInstaPageSettings)
  const licensePermissions: SocialNetworkLicensePermissions = useSelector(getLicensePermissions)
  const reNewPageSettings: boolean = useSelector(getReNewPageSettings)

  useEffect(() => {
    selectedTab === 'Facebook' && loadPageSettings(NetworkType.Facebook)
    selectedTab === 'Instagram' && loadPageSettings(NetworkType.Instagram)
  }, [reNewPageSettings],
  )

  useEffect(() => {
    if (selectedTab === NetworkType.Facebook) {
      setTopicEmail(storedCoreFBPageSettings?.notificationMailAddress || '')
    } else {
      setTopicEmail(storedCoreInstaPageSettings?.notificationMailAddress || '')
    }
  }, [selectedTab, storedCoreFBPageSettings, storedCoreInstaPageSettings],
  )

  const topicChangeTabListener = (({ detail }: CustomEvent) => {
    setSelectedTab(detail)
  }) as EventListener

  const loadPageSettings = (networkType: NetworkType) => {
    switch (networkType) {
    case NetworkType.Facebook:
      if (facebookPage?.networkPageId) {
        const params: GetCorePageSettingsRequest = {
          networkType: NetworkType.Facebook,
          networkPageId: facebookPage?.networkPageId,
        }
        dispatchGetCoreFBPageSettings(params)
      } else {
        dispatchDeleteCoreFBPageSettings()
      }
      return
    case NetworkType.Instagram:
      if (instagramPage?.networkPageId) {
        const params: GetCorePageSettingsRequest = {
          networkType: NetworkType.Instagram,
          networkPageId: instagramPage?.networkPageId,
        }
        dispatchGetCoreInstaPageSettings(params)
      } else {
        dispatchDeleteCoreInstaPageSettings()
      }
    }
  }

  useEffect(() => {
    window.addEventListener('ProfessocialTopicChangeTab', topicChangeTabListener)
    return (() => {
      window.removeEventListener('ProfessocialTopicChangeTab', topicChangeTabListener)
    })
  }, [],
  )

  useEffect(() => {
    loadPageSettings(NetworkType.Facebook)
  }, [facebookPage],
  )

  useEffect(() => {
    loadPageSettings(NetworkType.Instagram)
  }, [instagramPage],
  )

  const submitUpdateEmail = () => {
    if (selectedTab === 'Facebook') {
      if (topicEmail && facebookPage) {
        dispatchUpdateCorePageSettings({
          networkType: NetworkType.Facebook,
          networkPageId: facebookPage.networkPageId,
          corePageSettings: {
            notificationMailAddress: topicEmail,
          },
        })
      }
    } else {
      if (topicEmail && instagramPage) {
        dispatchUpdateCorePageSettings({
          networkType: NetworkType.Instagram,
          networkPageId: instagramPage.networkPageId,
          corePageSettings: {
            notificationMailAddress: topicEmail,
          },
        })
      }
    }
  }

  return (
    <Grid container>
      <Grid item container xs={7}>
        <Typography className={classes.text}>
          {t('coreDashboard:topicDescriptionFirst')}
        </Typography>
        <Typography className={classes.text2}>
          {t('coreDashboard:topicDescriptionSecond')}
        </Typography>
        <Stack direction='row' alignItems='center' spacing={2} sx={{ mb: 3 }}>
          <Typography sx={{ p: 1 }}><b>{t('topicsCommon:aboEmail')}</b></Typography>
          <TextFieldComponent
            id='topicEmail'
            rows={6}
            value={topicEmail || ''}
            size='small'
            variant='outlined'
            margin='dense'
            onChange={(e) => setTopicEmail(e.target.value)}
          />
          <Box>
            <ButtonComponent
              text={t('topicsCommon:save')}
              className={classes.emailButton}
              color='primary'
              variant='contained'
              size='medium'
              disabled={!(topicEmail && validateEmail(topicEmail))}
              onClick={submitUpdateEmail}
            />
          </Box>
        </Stack>
      </Grid>
      <Provider store={storeTopics}>
        {facebookPage && (
          <ProfessocialTopics topicConfiguration={{
            theme,
            integrateImages: true,
            editMode: DisplayMode.newScreen,
            editable: false,
            networkPage: {
              facebookPage: facebookPage,
              instagramPage: instagramPage,
            },
            licensePermissions: licensePermissions
          }}
          />
        )}
      </Provider>
    </Grid>
  )
}
export default UserTopics
